import React from 'react';
import PropTypes from 'prop-types';
import { connect } from '@gasbuddy/react';
import _ from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen } from '@fortawesome/free-solid-svg-icons/faPen';
import { Button } from '@gasbuddy/react-components';
import Modal from '../../modal';
import StatusIcons from './status-icons';
import styles from './styles.css';
import GridColumns from '../../grid-columns';
import RadioGroup from '../../radio-group';
import Notification from '../../notification';
import { mapEmergencyStatusesToObject } from './helper';
import StationPropType from '../../../utils/prop-types/station';
import { editStation } from '../../../actions';

const { api, logError, isFeatureEnabled } = require('../../../../lib/bputils').default;

const statusRadioOptions = [
  { label: 'Open for Business', value: 'open' },
  { label: 'Temporarily Closed', value: 'closed' },
  { label: 'Permanently Closed', value: 'closed_permanently' },
];

function EditContent({
  station,
  buttonClassName,
  dispatchEditStation,
  willSave,
  onSaved,
}) {
  const [openStatus, setOpenStatus] = React.useState(station.open_status);
  const [emergencyStatus, setEmergencyStatus] = React.useState(station.emergency_status);

  const handleOnOpenStatusChange = (e) => {
    const newOpenStatus = e.value;
    setOpenStatus(newOpenStatus);
  };

  const handleOnEmergencyStatusChange = (e) => {
    e.preventDefault();
    const { dataset: { value: newEmergencyStatus }, innerText } = e.currentTarget;

    let changedEmergencyStatus = _.cloneDeep(emergencyStatus);

    if (_.find(emergencyStatus, { id: newEmergencyStatus })) {
      // remove the emergency status from the array
      changedEmergencyStatus = _.remove(changedEmergencyStatus, es => (es.id !== newEmergencyStatus));
    } else {
      changedEmergencyStatus.push({ id: newEmergencyStatus, display_name: innerText });
    }
    setEmergencyStatus(changedEmergencyStatus);
  };

  const handleOnSave = async () => {
    willSave();

    try {
      const [newOpenStatus, newEmergencyStatus] = await Promise.all([
        saveOperationStatus(),
        saveEmergencyStatuses(),
      ]);

      dispatchEditStation({ open_status: newOpenStatus, emergency_status: newEmergencyStatus });
      Notification.success('Station status updated!');
    } catch (err) {
      logError(err);
      Notification.error('There was an error updating station status. Please contact support.');
    }
    onSaved();
  };

  const saveEmergencyStatuses = async () => {
    if (isFeatureEnabled('emergency_features') && emergencyStatus) {
      const { id: stationId, features } = station;
      const featureIds = [].concat(features, emergencyStatus).map(sf => sf.id);
      await api.put({
        path: `/stations/${stationId}/features`,
        data: { feature_ids: featureIds },
      });
    }

    return emergencyStatus;
  };

  const saveOperationStatus = async () => {
    const { id: stationId } = station;

    await api.put({
      path: `/stations/${stationId}/information`,
      data: { open_status: openStatus },
    });

    return openStatus;
  };

  const handleOnModalClose = () => {
    const { emergency_status: emergStatus, open_status: status } = station;
    setEmergencyStatus(emergStatus);
    setOpenStatus(status);
  };

  return (
    <Modal
      title="EDIT STATION STATUS"
      saveButtonText="Save Changes"
      onClose={handleOnModalClose}
      onSave={handleOnSave}
      button={(
        <Button
          type="button"
          className={buttonClassName}
          link
        >
          <FontAwesomeIcon icon={faPen} />
        </Button>
      )}
    >
      <div className={styles.statusMain}>
        <GridColumns contents={[
          {
            key: 'selectedOpenStatus',
            body: (
              <RadioGroup
                name="selectedOpenStatus"
                options={statusRadioOptions}
                handleOnChange={handleOnOpenStatusChange}
                selectedValue={openStatus}
              />
            ),
          },
          ...((isFeatureEnabled('emergency_features') && emergencyStatus) ? (
            [{
              key: 'selectedEmergencyStatus',
              body: (
                <div className={styles.statusContainer}>
                  <StatusIcons
                    emergencyStatusObject={mapEmergencyStatusesToObject(emergencyStatus)}
                    handleOnChangeIcons={handleOnEmergencyStatusChange}
                    editMode
                  />
                </div>
              ),
            }]
          ) : []),
        ]}
        />
      </div>
    </Modal>
  );
}

EditContent.propTypes = {
  station: StationPropType,
  buttonClassName: PropTypes.string,
  dispatchEditStation: PropTypes.func,
  willSave: PropTypes.func,
  onSaved: PropTypes.func,
};

EditContent.defaultProps = {
  station: null,
  buttonClassName: '',
  dispatchEditStation: null,
  willSave: null,
  onSaved: null,
};

const mapStateToProps = state => ({
  station: state.station,
});

const mapDispatchToProps = dispatch => ({
  dispatchEditStation: stationEdit => dispatch(editStation(stationEdit)),
});

export default connect(mapStateToProps, mapDispatchToProps)(EditContent);
